import _ from 'lodash'
import { useFormik } from 'formik'
import { useState, useEffect, useMemo } from 'react'
import AddIcon from '@material-ui/icons/Add'
import Box from '@material-ui/core/Box'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'

import { ConvertLocalTimePost, ConvertLocalTimeGet } from '~/utils'
import { ExecutionAPI, MasterDataAPI, ServiceRequestAPI } from '~/api'
import { InlineEditableGrid, ModalUpload, ButtonGrid, Search, Button, InputDate } from '~/components'
import { Colors } from '~/styles'

import SC from '../styled.cargo-equipment'
import { useCalculateTableWidth } from '../functions.cargo-equipment'

import gridColumns from './column'
import constants from './constants.cargo'
import { Alerts, ModalDel } from '../../../../components'
import moment from 'moment'
import union from 'lodash/union'

const WM_SOURCE_OPTIONS = [
  { value: 'receiving', label: 'Receiving' },
  { value: 'goodsIssue', label: 'Goods Issue' },
  { value: 'inventory', label: 'Inventory' }
]

const WM_SOURCE_META = {
  receiving: {
    label: 'Receiving',
    placeholder: 'Receipt Number',
    paramName: 'receiptNumber',
    dateFromLabel: 'Receive Date From',
    dateToLabel: 'Receive Date To',
    dateFromParam: 'receiptDateFrom',
    dateToParam: 'receiptDateTo',
    resultColumns: [
      { field: 'receiptNumber', title: 'Receive No' },
      { field: 'receiptDate', title: 'Receive Date' },
      { field: 'ownerDescription', title: 'Owner' },
      { field: 'poNumber', title: 'PO Number' },
      { field: 'productGroup', title: 'Product Group' },
      { field: 'skuProductName', title: 'SKU Product' },
      { field: 'receivingQuantity', title: 'Quantity' },
      { field: 'uomDescription', title: 'UOM' },
      { field: 'totalWeight', title: 'Total Weight' },
      { field: 'tonnage', title: 'Tonnage' },
      { field: 'totalVolume', title: 'Total Volume' },
      { field: 'packageNumber', title: 'Package Number' },
      { field: 'warehouseName', title: 'Warehouse' },
      { field: 'receivedLocation', title: 'Received Location' }
    ]
  },
  goodsIssue: {
    label: 'Goods Issue',
    placeholder: 'Goods Issue Number',
    paramName: 'goodsIssueNumber',
    dateFromLabel: 'Goods Issue Date From',
    dateToLabel: 'Goods Issue Date To',
    dateFromParam: 'goodsIssueDateFrom',
    dateToParam: 'goodsIssueDateTo',
    resultColumns: [
      { field: 'goodsIssueNumber', title: 'Goods Issue Number' },
      { field: 'goodsIssueDate', title: 'Goods Issue Date' },
      { field: 'ownerDescription', title: 'Owner' },
      { field: 'warehouseName', title: 'Warehouse' },
      { field: 'packageNumber', title: 'Package Number' },
      { field: 'packageType', title: 'Package Type' },
      { field: 'productGroup', title: 'Product Group' },
      { field: 'skuProductName', title: 'SKU Product' },
      { field: 'quantity', title: 'Quantity' },
      { field: 'uomDescription', title: 'UOM' }
    ]
  },
  inventory: {
    label: 'Inventory',
    placeholder: 'SKU Product Name',
    paramName: 'skuProductName',
    dateFromLabel: 'Receipt Date From',
    dateToLabel: 'Receipt Date To',
    dateFromParam: 'receiptDateFrom',
    dateToParam: 'receiptDateTo',
    resultColumns: [
      { field: 'ownerDescription', title: 'Owner' },
      { field: 'warehouseName', title: 'Warehouse' },
      { field: 'locationName', title: 'Location' },
      { field: 'productGroup', title: 'Product Group' },
      { field: 'skuProductName', title: 'SKU Product' },
      { field: 'uomDescription', title: 'UOM' },
      { field: 'receiptDate', title: 'Received Date' },
      { field: 'availableQuantity', title: 'Available Quantity' },
      { field: 'onHandQuantity', title: 'On Hand Quantity' },
      { field: 'receivedQuantity', title: 'Received Quantity' },
      { field: 'allocatedQuantity', title: 'Allocated Quantity' },
      { field: 'pickedQuantity', title: 'Picked Quantity' },
      { field: 'shippedQuantity', title: 'Shipped Quantity' },
      { field: 'poNumber', title: 'PO Number' },
      { field: 'packageNumber', title: 'Package Number' }
    ]
  }
}

const hasWMProductType = (productTypes = []) =>
  productTypes.some(
    (item) => item.description && item.description.toUpperCase() === 'WM'
  )

const formatWMDate = (dateValue) => {
  if (!dateValue) return ''
  return moment(dateValue).format('YYYY-MM-DD')
}

const mapWMRowToCargo = (item, source, productTypes = []) => ({
  ...constants.initValCargo,
  edit: true,
  isWMSource: true,
  source: 'wm',
  receiptNumber: source === 'receiving' ? item.receiptNumber || '' : '',
  goodsIssueNumber: source === 'goodsIssue' ? item.goodsIssueNumber || '' : '',
  receivingLinesId: source === 'receiving' ? item.receivingLinesId || '' : '',
  goodsIssueLinesId: source === 'goodsIssue' ? item.goodsIssueLinesId || '' : '',
  inventoryId: source === 'inventory' ? item.inventoryId || '' : '',
  product_name: item.skuProductName || '',
  product_type: hasWMProductType(productTypes) ? 'WM' : '',
  quantity:
    source === 'receiving'
      ? item.receivingQuantity ?? 0
      : source === 'goodsIssue'
      ? item.quantity ?? 0
      : null,
  uom_quantity: item.uomDescription || '',
  length: item.length ?? 0,
  width: item.width ?? 0,
  height: item.height ?? 0,
  uom_lwh: 'M',
  weight: item.weight ?? 0,
  uom_weight: 'Kg',
  volume: item.volume ?? 0,
  uom_volume: 'M3'
})

const getWMRowIdentifier = (item, source) => {
  if (source === 'receiving') return item?.receivingLinesId || ''
  if (source === 'goodsIssue') return item?.goodsIssueLinesId || ''
  if (source === 'inventory') return item?.inventoryId || ''
  return ''
}

const isWMRowAlreadyAdded = (item, source, cargoRows = []) => {
  const identifier = getWMRowIdentifier(item, source)
  if (!identifier) return false

  return cargoRows.some(
    (cargoRow) =>
      cargoRow?.receivingLinesId === identifier ||
      cargoRow?.goodsIssueLinesId === identifier ||
      cargoRow?.inventoryId === identifier
  )
}

const formatWMDisplayValue = (field, value) => {
  if (!value) return ''

  if (field.toLowerCase().includes('date')) {
    const formattedDate = moment(value).format('DD/MM/YYYY')
    return formattedDate === 'Invalid date' ? value : formattedDate
  }

  return value
}

const getWMCellValue = (column, item) => {
  if (column.field === 'tonnage') {
    return item?.totalWeight != null ? (item.totalWeight / 1000).toFixed(2) : ''
  }
  return formatWMDisplayValue(column.field, item?.[column.field])
}

const CargoGrid = ({ isEdit, dataAll }) => {
  const tableWidth = useCalculateTableWidth(gridColumns())

  const [id, setId] = useState(0)
  const [edit, setEdit] = useState(false)
  const [prodCode, setProdCode] = useState()
  const [material, setMaterial] = useState()
  const [dataDel, setDataDel] = useState({})
  const [inEdit, setInedit] = useState(false)
  const [cargoData, setCargoData] = useState([])
  const [newCargoData, setNewCargoData] = useState([])
  const [toggleDelete, setToggleDelete] = useState(false)
  const [modalDeleteAll, setModalDeleteAll] = useState(false)

  const [changeCargoNo, setChangeCargoNo] = useState(cargoNo)
  const [cargoNo, setCargoNo] = useState(dataAll.Cargo_No)
  const [uomData, setUomData] = useState(constants.initValUOM)
  const [inEditValues, setInEditValues] = useState(constants.initValCargo)
  const [showAlert, setAlert] = useState({
    visible: false,
    msg: '',
    success: false,
    type: ''
  })

  const [disabled, setDisabled] = useState(false)
  const [disabledCr, setDisabledCr] = useState(false)
  const [modalUpload, setModalUpload] = useState(false)
  const toggleModalUpload = () => setModalUpload(!modalUpload)

  const [cargoNoEdit, setCargoNoEdit] = useState(false)
  const [checkedCargos, setCheckedCargos] = useState([])
  const [checkedAllCargos, setCheckedAllCargo] = useState(false)

  const [search, setSearch] = useState('')
  const [addSearch, setAddSearch] = useState('')
  const [isWMPanelOpen, setIsWMPanelOpen] = useState(false)
  const [selectedWMSource, setSelectedWMSource] = useState('receiving')
  const [wmSearchKeyword, setWMSearchKeyword] = useState('')
  const [wmDateFrom, setWMDateFrom] = useState(null)
  const [wmDateTo, setWMDateTo] = useState(null)
  const [wmDateError, setWMDateError] = useState('')
  const [wmDateTouched, setWMDateTouched] = useState({
    wmDateFrom: false,
    wmDateTo: false
  })
  const [wmLoading, setWMLoading] = useState(false)
  const [wmData, setWMData] = useState([])
  const [wmInventoryFilters, setWMInventoryFilters] = useState({
    locationName: '',
    poNumber: '',
    packageNumber: ''
  })
  const [wmContext, setWMContext] = useState({
    clientCode: dataAll?.Client_Code || '',
    locationProjectName: dataAll?.Location || ''
  })

  const onKeyPress = (e) => {
    if (e.charCode == 13) {
      setAddSearch(search)
    }
  }

  const toggleEdit = () => {
    setCargoNoEdit(!cargoNoEdit)
    setChangeCargoNo(cargoNo)
  }

  const reindexCargoRows = (rows = []) =>
    rows.map((item, index) => ({
      ...item,
      editIndex: index
    }))

  const getNextDraftCargoId = (rows = cargoData) => {
    const draftIds = rows
      .map((item) => Number(item?.cargo_detail_id))
      .filter((item) => Number.isFinite(item) && item < 0)

    return draftIds.length > 0 ? Math.min(...draftIds) - 1 : -1
  }

  const getNextDraftProductNumber = (rows = cargoData) => {
    const productNumbers = rows
      .map((item) => {
        if (!item?.product_number) return 0
        const parts = String(item.product_number).split('-')
        const suffix = Number(parts[parts.length - 1])
        return Number.isNaN(suffix) ? 0 : suffix
      })
      .filter((item) => item > 0)

    const nextNumber = productNumbers.length > 0 ? Math.max(...productNumbers) + 1 : 1
    const typeNumber = dataAll.Sr_No.split('-').length == 1 ? '-0' : ''

    return `${dataAll.Sr_No}${typeNumber}-MAT-${nextNumber}`
  }

  const createDraftCargoRow = (baseRow = constants.initValCargo) => ({
    ...baseRow,
    edit: true,
    isDraft: true,
    cargo_detail_id: getNextDraftCargoId(cargoData),
    product_number: getNextDraftProductNumber(cargoData)
  })

  const handleDraftChange = (rowItem, field, value) => {
    setCargoData((prev) =>
      reindexCargoRows(
        prev.map((item) =>
          item.cargo_detail_id === rowItem.cargo_detail_id
            ? {
                ...item,
                [field]: value
              }
            : item
        )
      )
    )
  }

  const getPendingCargoRows = () => cargoData.filter((item) => item.isDraft)

  const cargoSummary = useMemo(() => {
    const groupBy = (valueField, uomField) => {
      const map = {}
      cargoData.forEach((item) => {
        const uom = item[uomField] || '-'
        const val = parseFloat(item[valueField]) || 0
        map[uom] = (map[uom] || 0) + val
      })
      return Object.entries(map).map(([uom, total]) => ({ uom, total }))
    }
    return {
      quantity: groupBy('quantity', 'uom_quantity'),
      weight: groupBy('weight', 'uom_weight'),
      volume: groupBy('volume', 'uom_volume')
    }
  }, [cargoData])

  const handleSaveAllCargo = async () => {
    const pendingCargoRows = getPendingCargoRows()

    if (pendingCargoRows.length === 0) return

    setDisabled(true)
    try {
      const payloadRows = pendingCargoRows.map((item) => buildCargoRequestData(item))

      const result = await ExecutionAPI.InsertMultipleCargo({
        data: payloadRows.map((item) => item.data)
      })

      if (result?.result) {
        setInedit(false)
        setEdit(false)
        setId(0)
        setInEditValues(constants.initValCargo)
        formSubmitCargo.resetForm({ values: constants.initValCargo })
        await getData()
      }
    } catch (error) {
      return error
    } finally {
      setDisabled(false)
    }
  }

  const buildCargoRequestData = (values) => {
    let matCode =
      Array.isArray(material) && material.length > 0
        ? material.filter((f) => f.value == values.material_status)
        : ''

    let product = prodCode?.find((item) => item.description === values.product_type)

    return {
      data: {
        Header_Code: '',
        SR_Code: dataAll.Code,
        Product_Code: product ? product.code : '',
        ID: values.cargo_detail_id !== 0 ? values.cargo_detail_id : 0,
        Product_Type: values.product_type,
        Product_Name: values.product_name,
        Cargo_No: cargoNo,
        Quantity: parseFloat(values.quantity),
        UOM_Quantity: values.uom_quantity,
        Length: parseFloat(values.length),
        Width: parseFloat(values.width),
        Height: parseFloat(values.height),
        UOM_LWH: values.uom_lwh,
        Weight: parseFloat(values.weight),
        UOM_Weight: values.uom_weight,
        Diameter: parseFloat(values.diameter),
        UOM_Diameter: values.uom_diameter,
        Volume: parseFloat(values.volume),
        UOM_Volume: values.uom_volume,
        Dangerous_Goods: values.dangerous_goods,
        Product_Number: values.product_number,
        Material_Status: matCode.length > 0 ? matCode[0].code : '',
        Material_Date: values.material_date
          ? ConvertLocalTimePost(dataAll.Location, values.material_date)
          : '',
        Receipt_number: values.receiptNumber || null,
        Goods_issue_number: values.goodsIssueNumber || null,
        Receiving_lines_id: values.receivingLinesId || null,
        Goods_issue_lines_id: values.goodsIssueLinesId || null,
        Inventory_id: values.inventoryId || null,
        Source: values.source || 'manual',
        Created_date:
          values.created_date === ''
            ? ConvertLocalTimePost(dataAll.Location, new Date())
            : values.created_date,
        modified_date: ConvertLocalTimePost(dataAll.Location, new Date()),
        Vehicle_Number: values.vehicle_number,
        Driver_Name: values.driver_name
      }
    }
  }

  const resetWMPanel = () => {
    setIsWMPanelOpen(false)
    setSelectedWMSource('receiving')
    setWMSearchKeyword('')
    setWMDateFrom(null)
    setWMDateTo(null)
    setWMDateError('')
    setWMDateTouched({ wmDateFrom: false, wmDateTo: false })
    setWMInventoryFilters({ locationName: '', poNumber: '', packageNumber: '' })
    setWMData([])
    setWMLoading(false)
  }

  const loadWMContext = async () => {
    try {
      const response = await ServiceRequestAPI.GetSrByEmail(dataAll.Sr_No)
      const serviceRequest = response?.data?.[0] || {}

      setWMContext({
        clientCode: serviceRequest.Client_Code || dataAll.Client_Code || '',
        locationProjectName: serviceRequest.Location || dataAll.Location || ''
      })
    } catch (error) {
      setWMContext({
        clientCode: dataAll.Client_Code || '',
        locationProjectName: dataAll.Location || ''
      })
      return error
    }
  }

  const searchWMData = async (
    source = selectedWMSource,
    keyword = wmSearchKeyword,
    dateFrom = wmDateFrom,
    dateTo = wmDateTo
  ) => {
    if (!source) return

    const wmMeta = WM_SOURCE_META[source]
    if (!wmMeta) return

    setWMLoading(true)
    try {
      const clientCode = wmContext.clientCode || dataAll.Client_Code || ''
      const locationProjectName = wmContext.locationProjectName || dataAll.Location || ''
      let response
      const filters = {
        [wmMeta.paramName]: keyword || '',
        [wmMeta.dateFromParam]: formatWMDate(dateFrom),
        [wmMeta.dateToParam]: formatWMDate(dateTo)
      }

      if (source === 'receiving') {
        response = await ExecutionAPI.getReceiving(
          clientCode,
          locationProjectName,
          keyword || '',
          filters
        )
      } else if (source === 'goodsIssue') {
        response = await ExecutionAPI.getGoodsIssue(
          clientCode,
          locationProjectName,
          keyword || '',
          filters
        )
      } else {
        response = await ExecutionAPI.getInventory(
          clientCode,
          locationProjectName,
          keyword || '',
          {
            ...filters,
            locationName: wmInventoryFilters.locationName || '',
            poNumber: wmInventoryFilters.poNumber || '',
            packageNumber: wmInventoryFilters.packageNumber || ''
          }
        )
      }

      setWMData(response?.data || [])
    } catch (error) {
      setAlert({
        msg: 'Failed to load WM data',
        success: false,
        visible: true,
        type: 'delete'
      })
      return error
    } finally {
      setWMLoading(false)
    }
  }

  const openWMPanel = () => {
    setIsWMPanelOpen(true)
    setSelectedWMSource('receiving')
    setWMSearchKeyword('')
    setWMDateFrom(null)
    setWMDateTo(null)
    searchWMData('receiving', '', null, null)
  }

  const handleWMSourceChange = (event) => {
    const nextSource = event.target.value
    setSelectedWMSource(nextSource)
    setWMSearchKeyword('')
    setWMDateFrom(null)
    setWMDateTo(null)
    setWMDateError('')
    setWMDateTouched({ wmDateFrom: false, wmDateTo: false })
    setWMInventoryFilters({ locationName: '', poNumber: '', packageNumber: '' })
    searchWMData(nextSource, '', null, null)
  }

  const wmForm = {
    values: {
      wmDateFrom,
      wmDateTo
    },
    touched: wmDateTouched,
    errors: {
      wmDateTo: wmDateError
    },
    setFieldValue: (field, value) => {
      if (field === 'wmDateFrom') {
        setWMDateTouched((prev) => ({ ...prev, wmDateFrom: true }))
        setWMDateFrom(value)
        if (!value) {
          setWMDateTo(null)
          setWMDateError('')
          return
        }

        if (wmDateTo && moment(wmDateTo).isBefore(moment(value), 'day')) {
          setWMDateTo(null)
          setWMDateError('Date To cannot be earlier than Date From')
        } else {
          setWMDateError('')
        }
      }
      if (field === 'wmDateTo') {
        setWMDateTouched((prev) => ({ ...prev, wmDateTo: true }))
        if (value && wmDateFrom && moment(value).isBefore(moment(wmDateFrom), 'day')) {
          setWMDateTo(null)
          setWMDateError('Date To cannot be earlier than Date From')
          return
        }

        setWMDateTo(value)
        setWMDateError('')
      }
    },
    setFieldTouched: (field) => {
      if (field === 'wmDateFrom') {
        setWMDateTouched((prev) => ({ ...prev, wmDateFrom: true }))
      }
      if (field === 'wmDateTo') {
        setWMDateTouched((prev) => ({ ...prev, wmDateTo: true }))
      }
    }
  }

  const handleWMAddRow = async (item) => {
    setInedit(true)
    _getAllDataType()

    if (selectedWMSource === 'goodsIssue' && item.goodsIssueLinesId) {
      const relatedItems = wmData.filter(
        (wmItem) => wmItem.goodsIssueLinesId === item.goodsIssueLinesId
      )
      const itemsToAdd = relatedItems.length > 0 ? relatedItems : [item]

      setCargoData((prev) => {
        let accRows = [...prev]
        const newRows = itemsToAdd.map((wmItem) => {
          const newCargo = {
            ...mapWMRowToCargo(wmItem, selectedWMSource, prodCode || []),
            edit: true,
            isDraft: true,
            cargo_detail_id: getNextDraftCargoId(accRows),
            product_number: getNextDraftProductNumber(accRows)
          }
          accRows = [newCargo, ...accRows]
          return newCargo
        })
        return reindexCargoRows([...newRows, ...prev])
      })
    } else {
      const newCargo = createDraftCargoRow(mapWMRowToCargo(item, selectedWMSource, prodCode || []))
      setCargoData((prev) => reindexCargoRows([newCargo, ...prev]))
    }
  }

  const formSubmitCargo = useFormik({
    initialValues: inEditValues,
    enableReinitialize: true,
    validateOnChange: true,
    validate: (values) => {
      setInEditValues((prev) => {
        const isLWHChanging =
          prev.length !== values.length ||
          prev.width !== values.width ||
          prev.height !== values.height
        const volBasedOnLWH = values.length * values.width * values.height


        return {
          ...values,
          volume: isLWHChanging ? volBasedOnLWH : values.volume,
        }
      })
    },
    onSubmit: async (values) => {
      setDisabled(true)
      const dataSave = buildCargoRequestData(values)
      if (edit) {
        try {
          const result = await ExecutionAPI.EditCargo(dataSave)
          if (result.result) {
            setInedit(false)
            setId(0)
            setEdit(false)
            await getData()
          }
          setDisabled(false)
        } catch (error) {
          return error
        }
      } else {
        try {
          const result = await ExecutionAPI.InsertCargo(dataSave)
          if (result.result) {
            setCheckedAllCargo(false)
            setInedit(false)
            await getData()
          }
          setDisabled(false)
        } catch (error) {
          return error
        }
      }
    }
  })

  // handle check all cargo
  const handleCheckAllCargo = () => {
    const cargoIds = cargoData.map((f) => f.cargo_detail_id)

    setCheckedAllCargo(!checkedAllCargos)

    if (checkedAllCargos) {
      // reset when its true, and unchecked all cargo
      setCheckedCargos([])
    } else {
      // checked all checkboxes when its false
      const mergedCargos = union(checkedCargos, cargoIds)
      setCheckedCargos(mergedCargos)
    }
  }

  // handle check cargo single item
  const handleCheckCargo = (cargoId) => {
    const cargoIds = checkedCargos.find((f) => f === cargoId)
    let updatedCargos = []
    if (!cargoIds) {
      // Add new checklist data item
      updatedCargos = [...checkedCargos, cargoId]
      setCheckedCargos(updatedCargos)
    } else {
      // Unchecked data item
      updatedCargos = checkedCargos.filter((f) => f !== cargoId)
      setCheckedCargos(updatedCargos)
    }

    // set checked all checkbox when checkedCargos equal cargo
    const forcedCheckedAll = updatedCargos.length === cargoData.length
    setCheckedAllCargo(forcedCheckedAll)
  }

  const handleToggleDeleteAll = () => setModalDeleteAll(!modalDeleteAll)
  const handleToggleDelete = () => setToggleDelete(!toggleDelete)

  const handleDeleteAllCargo = async () => {
    if (checkedCargos.length === 0) return

    const FormData = {
      data_deleted: checkedCargos
    }

    const response = await ExecutionAPI.DeleteMultipleCargo(FormData)

    if (response && response.error == '') {
      getData(true)
      setCheckedAllCargo(false)
      setCheckedCargos([])
    }

    handleToggleDeleteAll()
  }

  const _generateProductNumber = (newData, cargo) => {
    let maxNumber,
      maxNumber1,
      maxNumber2,
      newID,
      data,
      typeNumber,
      productNumber

    maxNumber1 =
      newData.length > 0
        ? Math.max.apply(
            0,
            newData.map((e) => {
              return e.product_number !== '' ? e.cargo_detail_id : 0
            })
          )
        : 1
    maxNumber2 =
      cargo.length > 0
        ? Math.max.apply(
            0,
            cargo.map((e) => {
              return e.product_number !== '' ? e.cargo_detail_id : 0
            })
          )
        : 1

    maxNumber1 >= maxNumber2 && newData.length > 0
      ? ((maxNumber = maxNumber1), (data = newData))
      : ((maxNumber = maxNumber2), (data = cargo))

    newID = data.length > 0 ? maxNumber + 1 : maxNumber

    typeNumber = dataAll.Sr_No.split('-').length == 1 ? '-0' : ''

    productNumber =
      dataAll.Sr_No +
      typeNumber +
      '-MAT-' +
      (data.length > 0
        ? maxNumber !== 0 &&
          !_.isUndefined(
            data[data.findIndex((e) => e.cargo_detail_id === maxNumber)]
              .product_number
          )
          ? Number(
              data[
                data.findIndex((e) => e.cargo_detail_id === maxNumber)
              ].product_number.split('-')[3]
            ) + 1
          : 1
        : 1)

    return { product_number: productNumber, id: newID }
  }

  const _importCargo = async (dataImport, cargoData) => {
    let newCargo = []

    const submitCargo = async (values) => {
      let product = prodCode?.filter(
        (f) => f.description === values.product_type
      ) || []
      let materials =
        material && material.length > 0
          ? material.filter((mt) => mt.value == values.material_status)
          : []

      const dataForm = {
        data: {
          Header_Code: '',
          SR_Code: dataAll.Code,
          Product_Code: product.length !== 0 ? product[0].code : '',
          ID: values.cargo_detail_id !== 0 ? values.cargo_detail_id : 0,
          Product_Type: product.length !== 0 ? values.product_type : '',
          Product_Name: values.product_name,
          Cargo_No: cargoNo,
          Quantity: parseFloat(values.quantity),
          UOM_Quantity: values.uom_quantity,
          Length: parseFloat(values.length),
          Width: parseFloat(values.width),
          Height: parseFloat(values.height),
          UOM_LWH: values.uom_lwh,
          Weight: parseFloat(values.weight),
          UOM_Weight: values.uom_weight,
          Diameter: parseFloat(values.diameter),
          UOM_Diameter: values.uom_diameter,
          Volume: parseFloat(values.volume),
          UOM_Volume: values.uom_volume,
          Dangerous_Goods: values.dangerous_goods,
          Product_Number: values.product_number,
          Material_Status: materials.length > 0 ? materials[0].code : '',
          Material_Date: moment(values.material_date).utc().toISOString(),
          source: 'excel',
          created_date: ConvertLocalTimePost(dataAll.Location, new Date()),
          modified_date: ConvertLocalTimePost(dataAll.Location, new Date()),
          vehicle_number: values.vehicle_number,
          driver_name: values.driver_name,
          receiptNumber: values.receiptNumber || null,
          goodsIssueNumber: values.goodsIssueNumber || null,
          receivingLinesId: values.receivingLinesId || null,
          goodsIssueLinesId: values.goodsIssueLinesId || null,
          inventoryId: values.inventoryId || null,
          source: values.source || 'manual'
        }
      }

      values.post == 'edit'
        ? await ExecutionAPI.EditCargo(dataForm)
        : await ExecutionAPI.InsertCargo(dataForm)
    }

    if (cargoData.length !== 0) {
      dataImport.map((e) => {
        if (e.is_deleted === 'FALSE' && e.product_number === '') {
          const response = _generateProductNumber(newCargo, cargoData)
          newCargo.push({
            ...e,
            post: 'new',
            cargo_detail_id: response.id,
            product_number: response.product_number
          })
        } else if (e.is_deleted === 'FALSE' && e.product_number !== '') {
          const indexData = cargoData.findIndex(
            (record) => record.product_number === e.product_number
          )

          indexData !== -1
            ? (newCargo[indexData] = {
                ...e,
                post: 'edit',
                product_number: cargoData[indexData].product_number,
                cargo_detail_id: cargoData[indexData].cargo_detail_id
              })
            : null
        } else if (e.is_deleted === 'TRUE' && e.product_number !== '') {
          const indexData = cargoData.findIndex(
            (record) => record.product_number === e.product_number
          )

          indexData !== -1
            ? (newCargo[indexData] = {
                ...e,
                post: 'delete',
                cargo_detail_id: cargoData[indexData].cargo_detail_id
              })
            : null
        }
      })
    } else {
      dataImport.map((e) => {
        if (e.is_deleted === 'FALSE' && e.product_number === '') {
          const response = _generateProductNumber(newCargo, cargoData)
          newCargo.push({
            ...e,
            post: 'new',
            cargo_detail_id: response.id,
            product_number: response.product_number
          })
        }
      })
    }

    await newCargo.map((e) => {
      if (!_.isUndefined(e.post)) {
        e.post === 'new' || e.post === 'edit' ? submitCargo(e) : handleRemove(e)
      }
    })
    await getData()
  }

  const addCargo = () => {
    setInedit(true)
    _getAllDataType()
    const newCargo = createDraftCargoRow({
      ...constants.initValCargo,
      source: 'manual',
      isWMSource: false
    })
    setInEditValues(newCargo)
    setCargoData((prev) => reindexCargoRows([newCargo, ...prev]))
  }

  const handleSave = async() => {
    formSubmitCargo.submitForm()
  }

  const handleCancel = (item) => {
    if (item?.isDraft) {
      const remainingRows = cargoData.filter(
        (row) => row.cargo_detail_id !== item.cargo_detail_id
      )

      setCargoData(reindexCargoRows(remainingRows))
      if (remainingRows.filter((row) => row.isDraft).length === 0) {
        setInedit(false)
      }
      return
    }

    setEdit(false)
    setInedit(false)
    setId(0)
    setInEditValues({
      product_name: ''
    })
    getData()
  }

  const handleEdit = (item) => {
    setEdit(true)
    setInedit(true)
    setId(item.cargo_detail_id)
    const wantTobeUpdatedItem = {
      ...item,
      edit: true,
      material_date: item.material_date
        ? new Date(
            ConvertLocalTimeGet(
              dataAll.Location,
              item.material_date,
              'MM-DD-YYYY HH:mm'
            )
          )
        : item.material_date
    }
    const updatedData = cargoData.map((val, index) => {
      if (item.editIndex === val.editIndex)
        return { ...val, edit: true, editIndex: index }
      return { ...val, editIndex: index }
    })
    setInEditValues(wantTobeUpdatedItem)
    setCargoData(updatedData)
  }

  const handleRemove = async (item) => {
    if (item.goodsIssueLinesId) {
      const relatedCargos = cargoData.filter(
        (row) => row.goodsIssueLinesId === item.goodsIssueLinesId
      )
      if (relatedCargos.length > 1) {
        const cargoIds = relatedCargos.map((row) => row.cargo_detail_id)
        try {
          const response = await ExecutionAPI.DeleteMultipleCargo({ data_deleted: cargoIds })
          if (response && response.error == '') {
            setAlert({
              msg: 'Delete data has been successful',
              success: true,
              visible: true,
              type: 'delete'
            })
            getData(true)
            setToggleDelete(!toggleDelete)
            setCheckedCargos((prev) => prev.filter((id) => !cargoIds.includes(id)))
          } else {
            setAlert({
              msg: 'Cannot delete data',
              success: false,
              visible: true,
              type: 'delete'
            })
          }
        } catch (error) {
          return error
        }
        return
      }
    }

    const dataRemove = {
      data: {
        id: item.cargo_detail_id,
        modified_date: new Date()
      }
    }
    try {
      const result = await ExecutionAPI.DeleteCargo(dataRemove)
      if (result) {
        setAlert({
          msg: 'Delete data has been successful',
          success: true,
          visible: true,
          type: 'delete'
        })
        let remove = true
        getData(remove)
        setToggleDelete(!toggleDelete)

        // Reset deleted cargos checklist
        const indexCheckedCargo = checkedCargos.findIndex(
          (f) => f === item.cargo_detail_id
        )

        // if cargo is available, exclude the cargo_detail_id from checkedCargos
        if (indexCheckedCargo !== -1) {
          const excludeCargo = checkedCargos.filter(
            (f) => f !== item.cargo_detail_id
          )
          setCheckedCargos(excludeCargo)
        }
      } else {
        setAlert({
          msg: 'Cannot delete data',
          success: false,
          visible: true,
          type: 'delete'
        })
      }
    } catch (error) {
      return error
    }
  }

  const editCargoNo = async () => {
    setDisabledCr(true)
    const cargoSelected = cargoData.map((element) => {
      let ele = { ...element }
      const materialSelected = uomData.material_status.find(
        (e) => e.value === ele.material_status
      )

      if (materialSelected) {
        ele.material_status = materialSelected.code
      }
      return ele
    })

    const cargoOrdered =
      _.orderBy(cargoSelected, ['cargo_detail_id'], ['asc']) ?? []

    const edt = {
      data: {
        Header_Code: '',
        Cargo_No: changeCargoNo,
        SR_Code: dataAll.Code,
        created_date: dataAll.Created_Date,
        modified_date: ConvertLocalTimePost(dataAll.Location, new Date()),
        Cargo_Detail: cargoOrdered
      }
    }
    try {
      const result = await ExecutionAPI.EditCargoNo(edt)
      if (result.result) {
        setCargoNo(changeCargoNo)
        setCargoNoEdit(false)
        await getData()
      }
    } catch (error) {
      return error
    }
    setDisabledCr(false)
  }

  const getData = async (remove) => {
    try {
      const result = await ExecutionAPI.getCargoData(dataAll.Sr_No)
      if (remove) {
        const updatedResult = (result.data.data ?? []).map((item, index) => ({
          ...item,
          receiptNumber: item.receipt_number || item.receiptNumber || '',
          goodsIssueNumber: item.goods_issue_number || item.goodsIssueNumber || '',
          receivingLinesId: item.receiving_lines_id || item.receivingLinesId || '',
          goodsIssueLinesId: item.goods_issue_lines_id || item.goodsIssueLinesId || '',
          inventoryId: item.inventory_id || item.inventoryId || '',
          isWMSource:
            !!(
              item.receiving_lines_id ||
              item.goods_issue_lines_id ||
              item.inventory_id ||
              item.receivingLinesId ||
              item.goodsIssueLinesId ||
              item.inventoryId
            ),
          editIndex: index
        }))
        const orderedResult = _.orderBy(updatedResult, ['modified_date'], ['desc'])
        setCargoData(orderedResult)
        setNewCargoData(orderedResult)
      } else {
        const updatedResult = (result.data.data ?? []).map((item, index) => ({
          ...item,
          receiptNumber: item.receipt_number || item.receiptNumber || '',
          goodsIssueNumber: item.goods_issue_number || item.goodsIssueNumber || '',
          receivingLinesId: item.receiving_lines_id || item.receivingLinesId || '',
          goodsIssueLinesId: item.goods_issue_lines_id || item.goodsIssueLinesId || '',
          inventoryId: item.inventory_id || item.inventoryId || '',
          isWMSource:
            !!(
              item.receiving_lines_id ||
              item.goods_issue_lines_id ||
              item.inventory_id ||
              item.receivingLinesId ||
              item.goodsIssueLinesId ||
              item.inventoryId
            ),
          volume:
            item.volume && item.volume.length > 0
              ? parseFloat(item.volume).toFixed(3)
              : item.volume,
          editIndex: index
        }))
        const newResult = _.orderBy(updatedResult, ['modified_date'], ['desc'])
        setCargoData(newResult)
        setNewCargoData(newResult)
      }
    } catch (error) {
      return error
    }
  }

  const _getAllDataType = async () => {
    try {
      const response = await MasterDataAPI.GetMasterDataType()
      const dangerous_goods = await MasterDataAPI.GetFilteredMasterDataEnum(
        'good'
      )
      const material_status = await MasterDataAPI.GetFilteredMasterDataEnum(
        'material_status'
      )
      const product_type = await MasterDataAPI.GetMasterProductType()
      if (
        response &&
        dangerous_goods &&
        product_type &&
        response.data.message.data &&
        dangerous_goods.message.data &&
        material_status.message.data &&
        product_type.data.message.data
      ) {
        setUomData({
          uom_lwh: response.data.message.data.filter(
            (f) => f.uom_type == 'MUT1'
          ),
          uom_volume: response.data.message.data.filter(
            (f) => f.uom_type == 'MUT2'
          ),
          uom_weight: response.data.message.data.filter(
            (f) => f.uom_type == 'MUT3'
          ),
          uom_diameter: response.data.message.data.filter(
            (f) => f.uom_type == 'MUT1'
          ),
          uom_quantity: response.data.message.data.filter(
            (f) => f.uom_type == 'MUT4'
          ),
          product_type: product_type.data.message.data.sort((a, b) =>
            a.description.localeCompare(b.description)
          ),
          dangerous_goods: dangerous_goods.message.data.sort((a, b) =>
            a.value.localeCompare(b.value)
          ),
          material_status: material_status.message.data.sort((a, b) =>
            a.value.localeCompare(b.value)
          )
        })
        setProdCode(product_type.data.message.data)
        setMaterial(material_status.message.data)
      }
    } catch (error) {
      return error
    }
  }

  useEffect(() => {
    _getAllDataType()
    getData()
    loadWMContext()
  }, [dataAll?.Sr_No])

  const handleRemoveModal = (item, type) => (
    <div>
      <ModalDel
        id='m_actv_delete_mdl'
        toggleDialog={() =>
          type === 'all' ? handleToggleDeleteAll() : handleToggleDelete()
        }
        submitDelete={() =>
          type === 'all' ? handleDeleteAllCargo() : handleRemove(item)
        }
        deleteText='Are you sure to delete the data ?'
      />
    </div>
  )

  const setDataDelete = async (item) => {
    await setDataDel(item)
    handleToggleDelete()
  }

  return (
    <div>
      {toggleDelete && handleRemoveModal(dataDel, 'single')}
      {modalDeleteAll && handleRemoveModal(null, 'all')}
      <div>
        <SC.Title>Cargo</SC.Title>
      </div>
      <SC.TopContentCargo>
        <SC.TextFieldContainer>
          <TextField
            label=''
            style={{ width: '400px' }}
            disabled={!cargoNoEdit && !isEdit}
            value={cargoNoEdit ? changeCargoNo : cargoNo}
            onChange={isEdit && ((e) => setChangeCargoNo(e.target.value))}
            id='execution-mdl-tab-cargo-equipment-cargo_no-txt'
          />
          {!cargoNoEdit ? (
            <SC.IconContainer
              disable={!isEdit}
              onClick={isEdit && (() => toggleEdit())}
            >
              <SC.EditIcon disable={!isEdit} />
            </SC.IconContainer>
          ) : (
            <SC.IconContainer
              disable={!isEdit}
              onClick={isEdit && (() => toggleEdit())}
            >
              <SC.CancelIcon disable={!isEdit} />
            </SC.IconContainer>
          )}
          {cargoNoEdit && (
            <SC.IconContainer
              disable={!isEdit || disabledCr}
              onClick={
                isEdit &&
                (() => {
                  changeCargoNo.length > 60
                    ? setAlert({
                        msg: 'Cargo Number Max. 60 Characters.',
                        success: false,
                        visible: true,
                        type: 'delete'
                      })
                    : disabledCr
                    ? {}
                    : editCargoNo()
                })
              }
            >
              <SC.SaveIcon disable={!isEdit} status={disabledCr} />
            </SC.IconContainer>
          )}
        </SC.TextFieldContainer>
        {isEdit && (
          <ButtonGrid
            enableSearch
            inEdit={inEdit}
            onAdd={addCargo}
            enableAddFromWM={true}
            onAddFromWM={openWMPanel}
            search={search}
            enableUpload={true}
            enableDelete={checkedCargos.length > 0}
            onDelete={() => handleToggleDeleteAll()}
            onChangeSearch={(e) => {
              setSearch(e.target.value)
            }}
            onKeyPress={onKeyPress}
            placeholder='Search by Material Desc'
            onSearchClick={() => setAddSearch(search)}
            onClickReset={() => {
              setSearch('')
              setAddSearch('')
            }}
            reset={search.length > 0 ? true : false}
            onUpload={toggleModalUpload}
          />
        )}
      </SC.TopContentCargo>
      {isWMPanelOpen && (
        <SC.WMPanel>
          <SC.WMPanelHeader>
            <SC.WMPanelTitle>Add New From WM</SC.WMPanelTitle>
            <Button
              onClick={resetWMPanel}
              borderColor={Colors.softBlue}
              color='transparent'
            >
              <Box color={Colors.softBlue}> Close </Box>
            </Button>
          </SC.WMPanelHeader>
          <SC.WMPanelBody>
            <SC.WMFieldGroup>
              <TextField
                select
                variant='outlined'
                label='Data Source'
                value={selectedWMSource}
                onChange={handleWMSourceChange}
                size='small'
                style={{ width: 200 }}
              >
                {WM_SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </SC.WMFieldGroup>
            <SC.WMSearchRow>
              {selectedWMSource === 'inventory' ? (
                <>
                  <TextField
                    size='small'
                    variant='outlined'
                    placeholder='SKU Product Name'
                    value={wmSearchKeyword}
                    onChange={(e) => setWMSearchKeyword(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.charCode === 13) {
                        searchWMData(selectedWMSource, wmSearchKeyword, wmDateFrom, wmDateTo)
                      }
                    }}
                    style={{ width: 150 }}
                    InputProps={{ style: { height: 35 } }}
                  />
                  <TextField
                    size='small'
                    variant='outlined'
                    placeholder='Location Name'
                    value={wmInventoryFilters.locationName}
                    onChange={(e) => {
                      const value = e.target.value
                      setWMInventoryFilters((prev) => ({ ...prev, locationName: value }))
                    }}
                    onKeyPress={(e) => {
                      if (e.charCode === 13) {
                        searchWMData(selectedWMSource, wmSearchKeyword, wmDateFrom, wmDateTo)
                      }
                    }}
                    style={{ width: 130 }}
                    InputProps={{ style: { height: 35 } }}
                  />
                  <TextField
                    size='small'
                    variant='outlined'
                    placeholder='PO Number'
                    value={wmInventoryFilters.poNumber}
                    onChange={(e) => {
                      const value = e.target.value
                      setWMInventoryFilters((prev) => ({ ...prev, poNumber: value }))
                    }}
                    onKeyPress={(e) => {
                      if (e.charCode === 13) {
                        searchWMData(selectedWMSource, wmSearchKeyword, wmDateFrom, wmDateTo)
                      }
                    }}
                    style={{ width: 110 }}
                    InputProps={{ style: { height: 35 } }}
                  />
                  <TextField
                    size='small'
                    variant='outlined'
                    placeholder='Package Number'
                    value={wmInventoryFilters.packageNumber}
                    onChange={(e) => {
                      const value = e.target.value
                      setWMInventoryFilters((prev) => ({ ...prev, packageNumber: value }))
                    }}
                    onKeyPress={(e) => {
                      if (e.charCode === 13) {
                        searchWMData(selectedWMSource, wmSearchKeyword, wmDateFrom, wmDateTo)
                      }
                    }}
                    style={{ width: 130 }}
                    InputProps={{ style: { height: 35 } }}
                  />
                </>
              ) : (
                <TextField
                  size='small'
                  variant='outlined'
                  placeholder={WM_SOURCE_META[selectedWMSource]?.placeholder || 'Search WM'}
                  value={wmSearchKeyword}
                  onChange={(e) => setWMSearchKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.charCode === 13) {
                      searchWMData(selectedWMSource, wmSearchKeyword, wmDateFrom, wmDateTo)
                    }
                  }}
                  style={{ width: 200 }}
                  InputProps={{ style: { height: 35 } }}
                />
              )}
              <SC.WMDateFilters>
                <InputDate
                  label={WM_SOURCE_META[selectedWMSource]?.dateFromLabel || 'Date From'}
                  name='wmDateFrom'
                  form={wmForm}
                  readOnly={false}
                  value={wmDateFrom}
                />
                <InputDate
                  label={WM_SOURCE_META[selectedWMSource]?.dateToLabel || 'Date To'}
                  name='wmDateTo'
                  form={wmForm}
                  readOnly={!wmDateFrom}
                  value={wmDateTo}
                />
              </SC.WMDateFilters>
              <Button
                onClick={() => searchWMData(selectedWMSource, wmSearchKeyword, wmDateFrom, wmDateTo)}
                disable={!!wmDateError}
                borderColor={Colors.softBlue}
                color='transparent'
              >
                <Box color={Colors.softBlue}> Search </Box>
              </Button>
            </SC.WMSearchRow>
          </SC.WMPanelBody>
          <SC.WMResultsWrapper>
            {wmLoading ? (
              <SC.WMEmptyState>Loading WM data...</SC.WMEmptyState>
            ) : (() => {
              const wmMeta = WM_SOURCE_META[selectedWMSource]
              const visibleWMData = wmData.filter(
                (item) => !isWMRowAlreadyAdded(item, selectedWMSource, cargoData)
              )

              if (visibleWMData.length === 0) {
                return <SC.WMEmptyState>No WM data found.</SC.WMEmptyState>
              }

              return (
                <SC.WMResultsTable>
                  <SC.WMResultsHead>
                    <tr>
                      <SC.WMResultsHeaderCell>Action</SC.WMResultsHeaderCell>
                      {(wmMeta?.resultColumns || []).map((column) => (
                        <SC.WMResultsHeaderCell key={column.field}>
                          {column.title}
                        </SC.WMResultsHeaderCell>
                      ))}
                    </tr>
                  </SC.WMResultsHead>
                  <tbody>
                    {visibleWMData.map((item, index) => (
                      <tr key={`${selectedWMSource}-${index}`}>
                        <SC.WMResultsCell>
                          <SC.WMActionButton
                            type='button'
                            onClick={() => handleWMAddRow(item)}
                            aria-label={`Add ${wmMeta?.label || 'WM'} row`}
                          >
                            <AddIcon fontSize='small' />
                          </SC.WMActionButton>
                        </SC.WMResultsCell>
                        {(wmMeta?.resultColumns || []).map((column) => (
                          <SC.WMResultsCell key={column.field}>
                            {getWMCellValue(column, item)}
                          </SC.WMResultsCell>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </SC.WMResultsTable>
              )
            })()}
          </SC.WMResultsWrapper>
        </SC.WMPanel>
      )}
      {getPendingCargoRows().length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button onClick={handleSaveAllCargo} borderColor={Colors.softBlue} color='transparent'>
            <Box color={Colors.softBlue}> Save All </Box>
          </Button>
        </div>
      )}
      <div>
        <InlineEditableGrid
          forceEdit={isEdit}
          sticky={true}
          isFixedCell
          data={cargoData.filter(
            (c) =>
              c.product_name.toLowerCase().includes(addSearch.toLowerCase()) ||
              addSearch == ''
          )}
          rowData={uomData}
          tableHeight='250px'
          disabled={disabled}
          location={dataAll.Location}
          columns={gridColumns(
            handleCheckAllCargo,
            handleCheckCargo,
            checkedCargos,
            checkedAllCargos
          )}
          tableWidth={tableWidth}
          handleEdit={handleEdit}
          handleSave={handleSave}
          onDraftChange={handleDraftChange}
          hideSaveAction={getPendingCargoRows().length > 0}
          hideActionColumn={!isEdit}
          handleRemove={setDataDelete}
          handleCancel={handleCancel}
          formSubmit={formSubmitCargo}
        />
      </div>
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f2f2f2' }}>
        <SC.Title style={{ fontSize: 14 }}>Cargo Summary</SC.Title>
        <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', marginTop: 8 }}>
          {[
            { label: 'Total Quantity', items: cargoSummary.quantity },
            { label: 'Total Weight', items: cargoSummary.weight },
            { label: 'Total Volume', items: cargoSummary.volume }
          ].map(({ label, items }) => (
            <div key={label}>
              <SC.SubTitle>{label}</SC.SubTitle>
              <div style={{ marginTop: 4 }}>
                {items.length > 0
                  ? items.map(({ uom, total }) => (
                      <div key={uom} style={{ display: 'flex', fontSize: 13, marginTop: 2 }}>
                        <span style={{ minWidth: 80, textAlign: 'right', paddingRight: 10 }}>
                          {parseFloat(total.toFixed(3))}
                        </span>
                        <span>{uom}</span>
                      </div>
                    ))
                  : (
                      <div style={{ display: 'flex', fontSize: 13, marginTop: 2, color: '#999' }}>
                        <span style={{ minWidth: 80, textAlign: 'right', paddingRight: 10 }}>-</span>
                        <span>-</span>
                      </div>
                    )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <ModalUpload
        data={cargoData}
        params={dataAll}
        showModal={modalUpload}
        handleClose={toggleModalUpload}
        importCargo={async (e) => {
          await handleCancel()
          await _importCargo(e, newCargoData)
        }}
      />
      <Alerts
        type={showAlert.type}
        open={showAlert.visible}
        close={setAlert}
        msg={showAlert.msg}
        success={showAlert.success}
      />
    </div>
  )
}

export default CargoGrid
